import React, { useContext, useState } from 'react';
import useArray from '../hooks/useArray';

const AppContext = React.createContext();

/** Returns the AppContext value. Must be used inside AppProvider. */
export function useAppContext() {
    return useContext(AppContext);
}

/**
 * Provides global app state to the component tree.
 *
 * Context value:
 *   showChooseInstitution / setShowChooseInstitution — controls the "choose institution" modal
 *   showManuallyAddAccount / setShowManuallyAddAccount — controls the "manually add account" modal
 *   toasts — array state managed by useArray for toast notifications
 *   addToast(notification) — adds a toast and auto-removes it after a timeout
 *   removeToast(id) — removes a toast by its ID
 */
const AppProvider = ({ children }) => {
    const [showChooseInstitution, setShowChooseInstitution] = useState(false);
    const [showManuallyAddAccount, setShowManuallyAddAccount] = useState(false);
    const toasts = useArray([]);

    /**
     * Adds a toast notification and schedules its removal.
     * @param {{ timeout?: number, [key: string]: any }} notification - Toast data; optional `timeout` in ms (default 5000)
     * @returns {number} The generated toast ID
     */
    const addToast = (notification) => {
        const id = Math.random() * 1000;
        const timeout = notification.timeout || 5000; // Default to 5 seconds, allow custom timeout
        toasts.push({ ...notification, id });
        setTimeout(() => {
            toasts.filter(n => n.id !== id);
        }, timeout);

        return id;
    };

    /**
     * Removes a toast by its ID.
     * @param {number} id - The toast ID returned by addToast
     */
    const removeToast = (id) => {
        const index = toasts.array.findIndex(n => n.id === id);
        if (index !== -1) {
            toasts.remove(index);
        }
    };

    return (
        <AppContext.Provider
            value={{
                showChooseInstitution,
                setShowChooseInstitution,
                showManuallyAddAccount,
                setShowManuallyAddAccount,
                toasts,
                addToast,
                removeToast,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export default AppProvider;
